// ═══ 사진 전체화면 뷰어 (세로 스크롤 연속) ═══
// #288 — 검차 사진이 50~60장이라 한 장씩 넘기는 페이징이 비효율 → 모바일 앱처럼
// 세로로 쭉 스크롤하며 훑는 방식. photos 배열 + viewIndex/setViewIndex 컴포넌트 API 유지(호출부 무수정).
// 2026-09-01 (Jin) — 공유 리포트 페이지·앱 zoomable_image 와 같은 구조로 정렬:
//   - 본문은 1600px 뷰어 파생(/view?size=view, 원본 5~10MB 대신 수백 KB) + 썸네일 placeholder 위에 lazy.
//   - 분류 헤더 position:sticky 로 '지금 어느 그룹을 보는지' 상단 고정.
//   - 캡션(번호·분류·저장)은 사진 하단 플로팅. 동영상만 컨트롤 겹침 방지로 흐름 배치.
//   - 사진 클릭 → 단일 확대(클릭/더블클릭 토글 2.5x, 휠 확대, 드래그 이동). 저장은 원본 /download.
import React, { useEffect, useRef, useState } from 'react';
import { API, API_ORIGIN } from '../utils/api';
import { parseEvalPhotoUrl } from '../utils/evalPhotoUrl';

var VIDEO_RE = /\.(mp4|mov|avi|webm|3gp)$/i;
var TOP_BAR_H = 56;

// 원본은 서버 스트림 라우트를 <img>/<video> src 로 직접 사용 (fetch/blob 불가 — #288 CORS 사유).
// /view = /download 별칭 — '*/download' 를 차단하는 광고차단 확장 회피 (#288)
function evalPath(url) {
  var pp = parseEvalPhotoUrl(url);
  return pp ? API + '/eval/orders/' + pp.orderId + '/photos/' + encodeURIComponent(pp.filename) : null;
}
function fullSrcOf(url) {
  if (!url) return '';
  var base = evalPath(url);
  return base ? base + '/view' : url; // blob: 등 비표준 — 그대로 사용
}
// 뷰어본(1600px 파생) — eval 사진만. RFQ 등 비-eval url 은 원본 그대로.
function viewSrcOf(url) {
  var base = evalPath(url);
  return base ? base + '/view?size=view' : (url || '');
}
function thumbSrcOf(url) {
  var base = evalPath(url);
  return base ? base + '/thumb' : '';
}

// 분류 라벨/순서 — OrderDetail 그리드의 CATEGORY_META·ALIAS 와 동일 (한글 업로드 정규화 포함).
var CATEGORY_ALIAS = { '외관': 'exterior', '사고부위': 'damage', '이상부위': 'defect', '내관': 'interior', '하부엔진': 'undercarriage', '하부/엔진': 'undercarriage', '엔진': 'engine', '서류': 'documents', '차량등록증': 'registration', '등록증': 'registration', '엔진음': 'engine_sound' };
var CATEGORY_LABEL = { exterior: '외관', damage: '사고부위', defect: '이상부위', interior: '내관', undercarriage: '하부/엔진', engine: '엔진', documents: '서류', registration: '차량등록증', engine_sound: '엔진음', general: '기타' };
var CATEGORY_ORDER = ['exterior', 'interior', 'undercarriage', 'engine', 'damage', 'defect', 'registration', 'documents', 'engine_sound', 'general'];

function categoryOf(p) {
  var raw = (p && p.category) || '';
  if (!raw) return '';
  return CATEGORY_ALIAS[raw] || raw;
}

// photos 를 분류 순서로 재배열 — 각 항목은 원 인덱스(viewIndex 기준)를 유지한다.
// 분류가 하나도 없으면(RFQ 등) 헤더 없는 단일 그룹.
function buildGroups(photos) {
  var byCat = {};
  var seen = [];
  photos.forEach(function(p, i) {
    var cat = categoryOf(p) || 'general';
    if (!byCat[cat]) { byCat[cat] = []; seen.push(cat); }
    byCat[cat].push({ photo: p, index: i });
  });
  var ordered = CATEGORY_ORDER.filter(function(c) { return byCat[c]; })
    .concat(seen.filter(function(c) { return CATEGORY_ORDER.indexOf(c) < 0; }));
  var anyCategory = photos.some(function(p) { return !!categoryOf(p); });
  return ordered.map(function(cat) {
    return { key: cat, label: anyCategory ? (CATEGORY_LABEL[cat] || cat) : '', items: byCat[cat] };
  });
}

function PhotoLightbox(props) {
  var photos = props.photos || [];
  var viewIndex = props.viewIndex;
  var setViewIndex = props.setViewIndex;
  var isOpen = !(viewIndex === null || viewIndex === undefined) && !!photos[viewIndex];

  var itemRefs = useRef([]);
  var [zoom, setZoom] = useState(null); // { index } — 단일 확대 오버레이

  // 클릭한 사진에서 시작 — 열릴 때 / viewIndex 변경 시 해당 항목으로 즉시 점프.
  // 각 항목 래퍼가 minHeight 로 자리를 잡고 있어 로드 전에도 위치 계산이 안정적.
  useEffect(function() {
    if (!isOpen) return;
    var el = itemRefs.current[viewIndex];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [isOpen, viewIndex]);

  // Escape — 확대가 열려 있으면 확대만, 아니면 뷰어 닫기
  useEffect(function() {
    if (!isOpen) return;
    var onKey = function(e) {
      if (e.key !== 'Escape') return;
      if (zoom) setZoom(null); else setViewIndex(null);
    };
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [isOpen, setViewIndex, zoom]);

  if (!isOpen) return null;

  var close = function() { setViewIndex(null); };
  var download = function(e, url) {
    e.stopPropagation();
    var pp = parseEvalPhotoUrl(url);
    if (pp) window.open(API_ORIGIN + '/api/eval/orders/' + pp.orderId + '/photos/' + encodeURIComponent(pp.filename) + '/download', '_blank');
  };
  var groups = buildGroups(photos);
  var font = "'Pretendard',sans-serif";

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#0a0e14', zIndex: 9999,
      overflowY: 'auto', overflowX: 'hidden',
      overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch',
    }}>
      {/* 상단 고정 바 — sticky (스크롤 컨테이너 안). 분류 헤더가 이 아래에 sticky 로 붙는다. */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10, height: TOP_BAR_H,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', background: '#0a0e14',
      }}>
        <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 600, fontFamily: font }}>
          사진 {photos.length}장
        </div>
        <button onClick={close} style={{
          background: 'rgba(255,255,255,0.14)', border: 'none',
          color: '#fff', fontSize: 22, width: 40, height: 40, borderRadius: '50%', cursor: 'pointer',
        }}>×</button>
      </div>
      <div style={{ maxWidth: 'min(92vw, 900px)', margin: '0 auto', padding: '0 0 48px' }}>
        {groups.map(function(g) {
          return (
            <React.Fragment key={g.key}>
              {g.label && (
                <div style={{
                  position: 'sticky', top: TOP_BAR_H, zIndex: 9, background: '#0a0e14',
                  color: '#fff', fontSize: 13, fontWeight: 700, padding: '10px 4px',
                  borderBottom: '1px solid rgba(255,255,255,0.08)', fontFamily: font,
                }}>
                  {g.label}<span style={{ color: '#94a3b8', fontWeight: 600, marginLeft: 6 }}>{g.items.length}</span>
                </div>
              )}
              {g.items.map(function(it) {
                var p = it.photo;
                var i = it.index;
                var url = p.url || '';
                var isVideo = VIDEO_RE.test(p.filename || url);
                var thumb = thumbSrcOf(url);
                var caption = (i + 1) + ' / ' + photos.length + (g.label ? ' · ' + g.label : '') + (p.memo ? ' · ' + p.memo : '');
                var captionBar = (
                  <div style={isVideo ? {
                    display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px 9px',
                    background: 'rgba(255,255,255,0.06)',
                  } : {
                    position: 'absolute', left: 0, right: 0, bottom: 0,
                    display: 'flex', alignItems: 'center', gap: 10, padding: '18px 12px 9px',
                    background: 'linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0.62))', pointerEvents: 'none',
                  }}>
                    <span style={{ color: '#e2e8f0', fontSize: 12, flex: 1, fontFamily: font, wordBreak: 'break-all', textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
                      {caption}
                    </span>
                    {url && !url.startsWith('blob:') && parseEvalPhotoUrl(url) && (
                      <button
                        onClick={function(e) { download(e, url); }}
                        style={{
                          pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 4, color: '#fff', fontSize: 12,
                          background: 'rgba(255,255,255,0.22)', padding: '6px 14px', borderRadius: 6,
                          border: 'none', cursor: 'pointer', fontWeight: 600, flexShrink: 0, fontFamily: font,
                        }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        저장
                      </button>
                    )}
                  </div>
                );
                return (
                  <div
                    key={i}
                    ref={function(el) { itemRefs.current[i] = el; }}
                    style={{ position: 'relative', marginBottom: 2, scrollMarginTop: TOP_BAR_H + (g.label ? 42 : 0) }}
                  >
                    {isVideo ? (
                      <React.Fragment>
                        <div style={{ background: '#111' }}>
                          <video src={fullSrcOf(url)} controls preload="metadata" style={{ width: '100%', height: 'auto', display: 'block', maxHeight: '80vh' }} />
                        </div>
                        {captionBar}
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        {/* 썸네일(그리드에서 이미 캐시)을 먼저 깔고 그 위에 뷰어본을 lazy 로 얹는다 —
                            같은 원본이라 종횡비 동일 → 레이아웃 점프 없음. 클릭 → 단일 확대. */}
                        <div
                          onClick={function() { setZoom({ index: i }); }}
                          style={{ position: 'relative', background: '#111', minHeight: thumb ? 0 : '40vh', cursor: 'zoom-in' }}
                        >
                          {thumb && <img src={thumb} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: 'auto', display: 'block' }} />}
                          <img
                            src={viewSrcOf(url)} alt={p.memo || ''} loading="lazy" decoding="async"
                            onLoad={function(e) { e.currentTarget.style.opacity = '1'; }}
                            style={thumb
                              ? { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', opacity: 0, transition: 'opacity .15s' }
                              : { width: '100%', height: 'auto', display: 'block' }}
                          />
                        </div>
                        {captionBar}
                      </React.Fragment>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
      {zoom && photos[zoom.index] && (
        <ZoomOverlay
          url={photos[zoom.index].url || ''}
          label={(zoom.index + 1) + ' / ' + photos.length}
          onClose={function() { setZoom(null); }}
          onDownload={function(e) { download(e, photos[zoom.index].url || ''); }}
        />
      )}
    </div>
  );
}

// 단일 확대 — 뷰어본(1600px, 목록에서 이미 캐시) 한 장. 클릭/더블클릭 토글 2.5x, 휠 확대(1~4x), 드래그 이동.
function ZoomOverlay(props) {
  var [scale, setScale] = useState(1);
  var [tx, setTx] = useState(0);
  var [ty, setTy] = useState(0);
  var drag = useRef(null);
  var moved = useRef(false);
  var imgRef = useRef(null);

  var toggleAt = function(clientX, clientY) {
    if (scale > 1) { setScale(1); setTx(0); setTy(0); return; }
    var r = imgRef.current ? imgRef.current.getBoundingClientRect() : null;
    var s = 2.5;
    if (r) { setTx((r.left + r.width / 2 - clientX) * (s - 1)); setTy((r.top + r.height / 2 - clientY) * (s - 1)); }
    setScale(s);
  };
  var onMouseDown = function(e) {
    if (scale <= 1) return;
    drag.current = { x: e.clientX, y: e.clientY, tx: tx, ty: ty };
    moved.current = false;
    e.preventDefault();
  };
  var onMouseMove = function(e) {
    if (!drag.current) return;
    var dx = e.clientX - drag.current.x, dy = e.clientY - drag.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) moved.current = true;
    setTx(drag.current.tx + dx); setTy(drag.current.ty + dy);
  };
  var onMouseUp = function() { drag.current = null; };
  var onClick = function(e) {
    if (moved.current) { moved.current = false; return; }
    toggleAt(e.clientX, e.clientY);
  };
  var onWheel = function(e) {
    e.preventDefault();
    var next = Math.min(4, Math.max(1, scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
    if (next <= 1) { setTx(0); setTy(0); }
    setScale(next);
  };
  var font = "'Pretendard',sans-serif";
  return (
    <div
      onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,14,20,0.98)', zIndex: 10000, display: 'flex', flexDirection: 'column' }}
    >
      <div style={{ height: TOP_BAR_H, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', color: '#cbd5e1', fontSize: 13, fontFamily: font }}>
        <span>{props.label}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {parseEvalPhotoUrl(props.url) && (
            <button onClick={props.onDownload} style={{ color: '#fff', background: 'rgba(255,255,255,0.14)', border: 'none', padding: '6px 12px', borderRadius: 999, fontSize: 12, cursor: 'pointer', fontFamily: font }}>저장</button>
          )}
          <button onClick={props.onClose} style={{ background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff', fontSize: 22, width: 40, height: 40, borderRadius: '50%', cursor: 'pointer' }}>×</button>
        </span>
      </div>
      <div
        onClick={function(e) { if (e.target === e.currentTarget) props.onClose(); }}
        onWheel={onWheel}
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
      >
        <img
          ref={imgRef}
          src={viewSrcOf(props.url)} alt=""
          onMouseDown={onMouseDown} onClick={onClick} onDragStart={function(e) { e.preventDefault(); }}
          style={{
            maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', userSelect: 'none',
            transform: 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')',
            transformOrigin: 'center center', transition: drag.current ? 'none' : 'transform .2s ease',
            cursor: scale > 1 ? 'grab' : 'zoom-in',
          }}
        />
      </div>
    </div>
  );
}

export default PhotoLightbox;
